import { ESTIMATE_PC_MSG, RFQ_MSG, VENDOR_IMPORT_MSG, VENDOR_QUOTE_STATUS_LABEL, pickNamesOf, vendorQuoteStateLabel, vendorSaleChip, type EstimateRfqs, type VendorProgress, type VendorQuoteProgress } from "@genbaryoku/contract";
import { Link } from "react-router";
import { FoldBand, useFoldState } from "../../components/FoldBand.jsx";
import { Icon } from "../../components/Shell.jsx";
import { yen } from "../../lib/num.js";
import { hardhat } from "./EstimateTable.jsx";
import { t } from "../../labels.js";
import { RfqStatus } from "../rfq/parts.jsx";

/* ============================================================================
   外注見積の帯（#131、Figma D1 43:203）。たたんだ形が既定：
     「外注見積 n件　取込済 ○円　未取込 n行 ○円（0行なら「全行取込済」）」＋「＋ 外注見積登録」＋開閉（#140・#244）
   開くと外注先ごとの表（外注先／状態／取込済／未取込／売価 ／ 仕入れ／原本・取込）。
   #274（docs/99 §4、10/31 射場さん ★3・★4）：札は名詞（文の「〜ました」「〜を下回っています」「〜上」と「残り」をやめた）。
     状態の列は外注見積の一覧と同じ言葉（API の state＝v_vendor_quote_list）。売価の列の札は contract の vendorSaleChip
     （朱「原価割れ …」／藍「粗利 22,000円（20.0%）」）見積依頼に入れた行（依頼中）の外注先も並べる。
   外注見積が 0 件（依頼も無い）のときは1行の薄い帯（「外注見積を登録する」と「見積依頼書作成」。10/7 射場さん：見積依頼の入口）。
   数は API（GET /estimates/:id/vendor-progress＝v_vendor_quote_progress）の外注見積ごとの数を足すだけ（画面で行を数えない）
   使う機能（#149）：外注見積の取込（vendorQuote）と見積依頼（rfq）は別々。vendorQuote が OFF なら外注見積の段（登録・取り込んだ・残り）を出さず
   題は「見積依頼 n件」、rfq が OFF なら見積依頼の段（行・「見積依頼書」「見積依頼書作成」・未回答）を出さない（呼ぶ側が rfqs・requested を渡さない）。
   外注先の名前は略称（同じ略称が2社あるときだけ「略称（正式名）」）
   ============================================================================ */

/** name＝略称、full＝正式名（外注見積の外注先の名前と見くらべる） */
export type Requested = { vendorId: string; name: string; full: string; lines: string[] };
const readable = (q: VendorQuoteProgress) => q.status === "review" || q.status === "done" || q.status === "manual";
/** 状態の列（#274）：外注見積の一覧と同じ言葉（AI読み取り中／要確認／未反映／一部反映／反映済み／不採用）。古い API の答えで state が無ければ前の言葉 */
const stateText = (q: VendorQuoteProgress): string => (q.state ? vendorQuoteStateLabel(q.state, q.partial) : VENDOR_QUOTE_STATUS_LABEL[q.status]);

export function VendorStrip({ progress, requested, rfqs, groupId, readOnly, onBulk, onOriginal, onRfq, onCreateRfq, rfqLoading = false, vendorQuotes = true }: {
  progress: VendorProgress | undefined;
  requested: Requested[];
  /** 見積依頼（#119）：外注先ごとの依頼の状態。読めていなければ今までの「未送付」の行だけ */
  rfqs?: EstimateRfqs | undefined;
  groupId: string | null;
  readOnly: boolean;
  onBulk: (quoteId: string) => void;
  onOriginal: (quoteId: string) => void;
  /** 「見積依頼書」：その外注先の依頼を開く（無ければ下書きを作る） */
  onRfq?: ((vendorId: string) => void) | undefined;
  /** 空の帯の「見積依頼書作成」：外注先と行を選ぶ窓を開く（書ける見積・使う機能 rfq の会社だけ） */
  onCreateRfq?: (() => void) | undefined;
  /** 見積依頼の一覧を待っている間（#179：「見積依頼書作成」は先に同じ所に出して押せない形にする＝あとから入って「外注見積を登録する」をずらさない） */
  rfqLoading?: boolean;
  /** 使う機能「{vendorQuote}の取込」（#149）。false なら外注見積の段を出さない（見積依頼だけの帯） */
  vendorQuotes?: boolean;
}) {
  const [open, setOpen] = useFoldState("est.vendor", false); // #244：開け閉めはこの端末に覚える（たたんだ形が既定）
  const quotes = progress?.quotes ?? [];
  const sameAsQuote = (full: string) => quotes.some((q) => q.vendorName && full && q.vendorName === full);
  // 見積依頼の行：行に付いた外注先と、依頼を出した外注先（#119）。読めていなければ今までどおり 仕入れ未入力の行の外注先だけ
  const fromRfq = rfqs
    ? rfqs.vendors.filter((v) => v.lineCount > 0 || v.current).map((v) => ({ vendorId: v.vendor.id, name: v.vendor.shortName, full: v.vendor.name, lines: requested.find((r) => r.vendorId === v.vendor.id)?.lines ?? [], lineCount: v.lineCount, current: v.current }))
    : [];
  // いま付けたばかりの外注先（依頼の一覧を読み直す前）も出す
  const rfqRows = [
    ...fromRfq,
    ...requested.filter((r) => !fromRfq.some((x) => x.vendorId === r.vendorId) && (rfqs || !sameAsQuote(r.full))).map((r) => ({ vendorId: r.vendorId, name: r.name, full: r.full, lines: r.lines, lineCount: r.lines.length, current: null })),
  ];
  const rfqNames = pickNamesOf(rfqRows.map((r) => ({ short: r.name, full: r.full })));
  const reqs = rfqRows.filter((r) => !sameAsQuote(r.full));
  const register = groupId && vendorQuotes ? (
    <Link className="btn tonal sm" to={`/vendor-quotes?group=${groupId}&via=group`} data-testid="to-vendor-quotes">
      <Icon k="plus" />
      {t(ESTIMATE_PC_MSG.vendorRegister)}
    </Link>
  ) : null;
  if (!quotes.length && !rfqRows.length) {
    // 見積依頼だけの会社で、作成の口も無い（出した見積）なら帯ごと出さない
    if (!vendorQuotes && !onCreateRfq) return null;
    // #244：開閉の帯の 0件の形（見出しの1行だけ・開閉のボタンなし）
    return (
      <FoldBand
        id="est.vendor"
        className="vstrip empty"
        empty
        icon={hardhat}
        title={<span className="muted">{vendorQuotes ? t("{vendorQuote}") : RFQ_MSG.listTitle}</span>}
        testId="vendor-strip"
        actions={
          <>
            {register}
            {onCreateRfq ? (
              <button type="button" className="btn tonal sm" onClick={onCreateRfq} disabled={rfqLoading} aria-busy={rfqLoading || undefined} data-testid="band-rfq-create">
                <Icon k="doc" />
                {RFQ_MSG.create}
              </button>
            ) : null}
          </>
        }
      />
    );
  }
  const ready = quotes.filter(readable);
  const done = ready.reduce((a, q) => a + q.doneAmount, 0);
  const restN = ready.reduce((a, q) => a + q.restCount, 0);
  const restY = ready.reduce((a, q) => a + q.restAmount, 0);
  // #244（docs/94 B-5）：開閉の帯（FoldBand）。たたんだ形が既定・開け閉めはこの端末に覚える。数のしるしは開いても出す
  return (
    <FoldBand
      id="est.vendor"
      className={"vstrip" + (open ? " open" : "")}
      defaultOpen={false}
      open={open}
      onToggle={setOpen}
      icon={hardhat}
      title={<span data-testid="vendor-count">{vendorQuotes ? t(ESTIMATE_PC_MSG.vendorCount).replace("{n}", String(quotes.length + reqs.length)) : RFQ_MSG.bandCount.replace("{n}", String(rfqRows.length))}</span>}
      keepSummary
      summary={
        <>
          {rfqs?.waiting ? <span className="chip" data-testid="vendor-rfq-waiting">{`${RFQ_MSG.tabWaiting} ${rfqs.waiting}件`}</span> : null}
          {vendorQuotes ? <span className="muted num" data-testid="vendor-done">{ESTIMATE_PC_MSG.vendorDone.replace("{yen}", yen(done))}</span> : null}
          {!vendorQuotes ? null : restN ? (
            <span className="chip shu num" data-testid="vendor-rest">{ESTIMATE_PC_MSG.vendorRest.replace("{n}", String(restN)).replace("{yen}", yen(restY))}</span>
          ) : ready.length ? (
            <span className="chip" data-testid="vendor-rest">{ESTIMATE_PC_MSG.vendorAllDone}</span>
          ) : null}
        </>
      }
      actions={register}
      toggleTestId="vendor-toggle"
      testId="vendor-strip"
    >
      {(
        <table className="vstbl">
          <thead>
            <tr>
              <th>{t(ESTIMATE_PC_MSG.vendorColName)}</th>
              <th>{ESTIMATE_PC_MSG.vendorColState}</th>
              {vendorQuotes ? <th className="r">{ESTIMATE_PC_MSG.vendorColDone}</th> : null}
              <th>{vendorQuotes ? ESTIMATE_PC_MSG.vendorColRest : RFQ_MSG.cardItems}</th>
              <th>{vendorQuotes ? ESTIMATE_PC_MSG.vendorColSale : RFQ_MSG.colNo}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {quotes.map((q) => {
              const name = `${q.vendorName || t("{vendor}")} ${q.quoteNo}`.trim();
              if (!readable(q)) {
                return (
                  <tr key={q.vendorQuoteId} className="quiet" data-testid="vendor-band">
                    <td className="vn">{name}</td>
                    <td><span className="chip" data-testid="vendor-band-state">{stateText(q)}</span></td>
                    <td className="r muted">—</td>
                    <td />
                    <td />
                    <td />
                  </tr>
                );
              }
              const allDone = q.restCount === 0 && q.allCount > 0;
              const sale = vendorSaleChip(q, yen);
              return (
                <tr key={q.vendorQuoteId} className={sale?.tone === "shu" ? "shu" : undefined} data-testid="vendor-band">
                  <td className="vn">{name}</td>
                  <td><span className="chip" data-testid="vendor-band-state">{stateText(q)}</span></td>
                  <td className="r num">{yen(q.doneAmount)}</td>
                  <td>{allDone ? <span className="chip">{VENDOR_IMPORT_MSG.allDone}</span> : <span className="chip shu num">{ESTIMATE_PC_MSG.vendorRest.replace("{n}", String(q.restCount)).replace("{yen}", yen(q.restAmount))}</span>}</td>
                  <td className="num">
                    {q.doneCount > 0 ? `${yen(q.saleAmount)} ／ ${yen(q.costAmount)}` : <span className="muted">—</span>}
                    {sale ? <span className={sale.tone === "shu" ? "chip shu" : "chip vs-margin"} data-testid="vendor-band-sale">{sale.text}</span> : null}
                  </td>
                  <td className="acts">
                    <button type="button" className="btn sm" onClick={() => onOriginal(q.vendorQuoteId)} data-testid="band-orig">
                      <Icon k="doc" />
                      {ESTIMATE_PC_MSG.vendorOriginal}
                    </button>
                    {!readOnly && !allDone ? (
                      <button type="button" className="btn tonal sm" onClick={() => onBulk(q.vendorQuoteId)} data-testid="band-bulk">
                        {ESTIMATE_PC_MSG.vendorImport}
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {rfqRows.map((r, i) => (
              <tr key={"rq-" + r.vendorId} data-testid="vendor-requested">
                <td className="vn" title={r.full}>{rfqNames[i]}</td>
                <td>
                  {r.current ? (
                    <RfqStatus s={r.current} />
                  ) : (
                    <span className="rqchip is-unsent"><Icon k="clock" /><span>{ESTIMATE_PC_MSG.requestedState}</span></span>
                  )}
                </td>
                {vendorQuotes ? <td className="r muted">—</td> : null}
                <td className="muted" title={r.lines.join("・")}>
                  {r.lines.length ? `${r.lines.slice(0, 2).join("・")}${r.lines.length > 2 ? "…" : ""} ` : ""}
                  {ESTIMATE_PC_MSG.requestedRest.replace("{n}", String(r.lines.length || r.lineCount))}
                </td>
                <td className="muted num">{r.current?.no ?? "—"}</td>
                <td className="acts">
                  {onRfq ? (
                    <button type="button" className="btn tonal sm" onClick={() => onRfq(r.vendorId)} data-testid="band-rfq">
                      <Icon k="doc" />
                      {RFQ_MSG.open}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </FoldBand>
  );
}
